// ── menuEngineering.js — 메뉴 엔지니어링 4분면 분류 ────────────────
// 외식업 컨설팅 표준 분석법.
// 평균 판매수량(P) × 평균 마진율(M) 기준으로 4사분면 분류:
//   Star      (인기 ↑ + 마진 ↑) — 유지·홍보
//   Plowhorse (인기 ↑ + 마진 ↓) — 가격 ↑ 또는 원가 ↓
//   Puzzle    (인기 ↓ + 마진 ↑) — 마케팅 강화
//   Dog       (인기 ↓ + 마진 ↓) — 단종 검토
//
// 입력:
//   enrichedMenus[] — calcMenuProfitability + enrichWithActualSales 결과
//     필수 필드: marginPct, actualSales.qty
// 출력:
//   menus[] — { ...원본, quadrant: 'star'|'plowhorse'|'puzzle'|'dog', popularity, profitability }

export const QUADRANT_META = {
  star:      { label: '⭐ Star',      color: '#18181B', desc: '인기·마진 둘 다 ↑ — 유지·홍보' },
  plowhorse: { label: '🐴 Plowhorse', color: '#EA580C', desc: '인기 ↑ 마진 ↓ — 가격↑ 또는 원가↓' },
  puzzle:    { label: '🧩 Puzzle',    color: '#0369a1', desc: '마진 ↑ 인기 ↓ — 마케팅 강화' },
  dog:       { label: '🐕 Dog',       color: '#9ca3af', desc: '인기·마진 둘 다 ↓ — 단종 검토' },
};

/**
 * 4분면 분류
 * @param {Array} enrichedMenus
 * @param {object} options
 *   - popularityMode: 'mean' (평균) | 'median' (중앙값, 이상치 영향 ↓)
 *   - profitabilityMode: 'mean' | 'median'
 *   - minSamples: 분석 최소 메뉴 수 (기본 4)
 * @returns {object} { menus: Array, threshold: { popularity, profitability }, summary }
 */
export function classifyMenuEngineering(enrichedMenus, options = {}) {
  const {
    popularityMode = 'median',
    profitabilityMode = 'median',
    minSamples = 4,
  } = options;

  // 평가 가능한 메뉴만 (마진율 + 실제 판매수량 모두 있어야)
  const evaluable = (enrichedMenus || []).filter(m =>
    m && m.hasPrice && m.hasIngredients
    && m.marginPct !== null && m.marginPct !== undefined
    && m.actualSales && m.actualSales.qty > 0
  );

  if (evaluable.length < minSamples) {
    return {
      menus: evaluable.map(m => ({ ...m, quadrant: null })),
      threshold: { popularity: null, profitability: null },
      summary: {
        evaluableCount: evaluable.length,
        insufficientData: true,
        message: `분석에 메뉴 ${minSamples}개 이상 필요 (현재 ${evaluable.length}개)`,
      },
    };
  }

  // 임계값 계산
  const popularities = evaluable.map(m => m.actualSales.qty);
  const profitabilities = evaluable.map(m => m.marginPct);

  const popularityThreshold = popularityMode === 'median'
    ? median(popularities)
    : mean(popularities);
  const profitabilityThreshold = profitabilityMode === 'median'
    ? median(profitabilities)
    : mean(profitabilities);

  // 분류
  const classified = evaluable.map(m => {
    const popular = m.actualSales.qty >= popularityThreshold;
    const profitable = m.marginPct >= profitabilityThreshold;
    let quadrant;
    if (popular && profitable) quadrant = 'star';
    else if (popular && !profitable) quadrant = 'plowhorse';
    else if (!popular && profitable) quadrant = 'puzzle';
    else quadrant = 'dog';
    return { ...m, quadrant, popularity: m.actualSales.qty, profitability: m.marginPct };
  });

  // 사분면별 카운트
  const counts = {
    star: 0, plowhorse: 0, puzzle: 0, dog: 0,
  };
  for (const m of classified) counts[m.quadrant] += 1;

  return {
    menus: classified,
    threshold: {
      popularity: Math.round(popularityThreshold * 10) / 10,
      profitability: Math.round(profitabilityThreshold * 10) / 10,
    },
    summary: {
      evaluableCount: evaluable.length,
      counts,
      starShare: classified.length > 0 ? (counts.star / classified.length) : 0,
      dogShare: classified.length > 0 ? (counts.dog / classified.length) : 0,
    },
  };
}

// ── 통계 헬퍼 ──
function mean(arr) {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function median(arr) {
  if (!arr || arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * 사분면별 메뉴 그룹핑 (UI 카드용)
 */
export function groupByQuadrant(classified) {
  const groups = { star: [], plowhorse: [], puzzle: [], dog: [] };
  for (const m of classified) {
    if (m.quadrant) groups[m.quadrant].push(m);
  }
  // 각 그룹 내부 정렬: 매출 내림차순
  for (const q of Object.keys(groups)) {
    groups[q].sort((a, b) =>
      (b.actualSales?.revenue || 0) - (a.actualSales?.revenue || 0)
    );
  }
  return groups;
}

/**
 * 사분면별 액션 추천 메시지
 */
export function quadrantActionMessage(quadrant, menu) {
  if (!menu) return '';
  switch (quadrant) {
    case 'star':
      return `🌟 매장의 효자. 재고·품질·구색을 최우선으로 챙기세요.`;
    case 'plowhorse':
      return `💪 잘 팔리지만 마진이 약함. 판매가 +5~10% 인상이나 원가 절감 (식자재 단가 협상·공동구매)을 검토하세요.`;
    case 'puzzle':
      return `🧩 수익성은 좋지만 안 팔림. 메뉴판 위치 상단 이동·세트 구성·SNS 홍보로 노출 ↑.`;
    case 'dog':
      return `🐕 인기·마진 둘 다 부족. 단종 검토하거나 레시피 전면 개편 필요. 재고 부담만 늘림.`;
    default:
      return '';
  }
}
